/**
 * Authentication Utilities
 * 
 * Provides consistent methods for retrieving and working with authentication data
 * across the application.
 */

import AuthService from '../services/AuthService';

// Mock user data for development without authentication
const MOCK_USER_DATA = {
  user_id: 'mock-user-123',
  user_name: 'Mock User',
  email: 'mock@example.com',
  roles: ['user']
};

// Whether to use mock data when no authentication is present
const AUTH_BYPASS_ENABLED = true;

/**
 * Get the current authenticated user data from all possible sources
 * @returns {Object|null} User data object or null if not authenticated
 */
export function getCurrentUser() {
  let userData = null;
  
  // Try AuthService first
  try {
    userData = AuthService.getPostAuthData()?.userData;
    if (userData && userData.user_id) {

      return userData;
    }
  } catch (e) {
    console.warn('Error retrieving user from AuthService:', e);
  }
  
  // Try sessionStorage directly
  try {
    const sessionData = sessionStorage.getItem('authData');
    if (sessionData) {
      const parsedData = JSON.parse(sessionData);
      userData = parsedData.userData;
      if (userData && userData.user_id) {

        return userData;
      }
    }
  } catch (e) {
    console.warn('Error retrieving user from sessionStorage:', e);
  }
  
  // Try window.AuthService as fallback
  try {
    if (window.AuthService && typeof window.AuthService.getPostAuthData === 'function') {
      const windowAuthData = window.AuthService.getPostAuthData();
      userData = windowAuthData?.userData;
      if (userData && userData.user_id) {

        return userData;
      }
    }
  } catch (e) {
    console.warn('Error accessing window.AuthService:', e);
  }
  
  // Use mock data if enabled and no real auth found
  if (AUTH_BYPASS_ENABLED) {
    // Using mock user data in bypass mode
    return MOCK_USER_DATA;
  }
  
  return null;
}

/**
 * Enhance an object with user information from the current authenticated user
 * @param {Object} obj - Object to enhance with user data
 * @returns {Object} Enhanced object with user_id and userName
 */
export function addUserInfoToObject(obj) {
  const user = getCurrentUser();
  
  if (!user) {
    console.error('No authenticated user found');
    return obj;
  }
  
  return {
    ...obj,
    // Use both formats for backwards compatibility
    user_id: user.user_id,
    userId: user.user_id,
    userName: user.user_name || user.username || user.name || 'Unknown User'
  };
}

/**
 * Check if a user is authenticated
 * @returns {boolean} True if a user is authenticated
 */
export function isAuthenticated() {
  return getCurrentUser() !== null;
}

/**
 * Get a standardized user object with consistent property names
 * @param {Object} userObj - User object with potentially inconsistent property names
 * @returns {Object} Standardized user object
 */
export function standardizeUserObject(userObj) {
  if (!userObj) return null;
  
  return {
    user_id: userObj.user_id || userObj.userId || userObj.id,
    user_name: userObj.user_name || userObj.userName || userObj.username || userObj.name || 'Unknown User',
    email: userObj.email,
    roles: userObj.roles || []
  };
}

// Create a named export object
const AuthUtils = {
  getCurrentUser,
  addUserInfoToObject,
  isAuthenticated,
  standardizeUserObject,
  MOCK_USER_DATA
};

export default AuthUtils;
